import { Api } from '@/core/api.js'
import Vue from 'vue'
import store from '@/store'

import { TIMERPRODUCT_STATUS, TIMER_TICK_FAST_MS, TIMER_TICK_SLOW_MS, TOOLS } from '@/constants'
import { collectExpiredTimers, collectRestockProducts, evaluateRecommendationTriggers } from '@/utils/timerProductRecommendations'
import { buildDefaultLocalKpiCounts, buildRadarModuleState } from '@/store/utils/radarKpis.js'

import i18n from '@/i18n'
const api = new Api()
import { db_timerproducts, db_tpcategories, db_tpstates, db_timerlogs } from '@/database'
import { referenceCanMovePast } from '@/utils/referenceCanMovePast.js'
import { buildCaducidadSnapshotsForParentFromChild } from '@/utils/caducidadSnapshotsForParentFromChild.js'
import { isChannelOnlyMove, captureTimerDateSnapshot, applyTimerDateSnapshot, syncTimerDateStringsFromTimestamps, applyTimerDateFieldsFromPatch } from '@/utils/timerProductChannelMove.js'
import { nextOfflineTimerIdentity } from '@/utils/timerProductTemporalCollection'

function getMinMaxStateOrder(productStates) {
    let minOrder = Infinity
    let maxOrder = -Infinity
    if (!productStates) {
        return { minOrder: NaN, maxOrder: NaN }
    }
    Object.keys(productStates).forEach((id) => {
        const o = parseInt(productStates[id].order, 10)
        if (!isNaN(o)) {
            minOrder = Math.min(minOrder, o)
            maxOrder = Math.max(maxOrder, o)
        }
    })
    if (minOrder === Infinity || maxOrder === -Infinity) {
        return { minOrder: NaN, maxOrder: NaN }
    }
    return { minOrder, maxOrder }
}

function getStateOrder(productStates, stateId) {
    if (!productStates || !stateId || !productStates[stateId]) {
        return NaN
    }
    return parseInt(productStates[stateId].order, 10)
}

/**
 * Aplica caducidad (`date` / `date_timestamp` y derivados) desde un unix en segundos.
 */
function applyCaducidadFromUnix(data, unixTs) {
    if (unixTs == null || unixTs === '') {
        return
    }
    const ts = typeof unixTs === 'string' ? parseInt(unixTs, 10) : unixTs
    if (isNaN(ts) || ts <= 0) {
        return
    }
    const m = moment(ts * 1000)
    if (!m.isValid()) {
        return
    }
    data.date_timestamp = ts
    data.date = m.format('DD/MM/YYYY HH:mm:ss')
    data.date_day = m.format('DD/MM/YYYY')
    data.date_time = m.format('H:mm')
    for (let i = 1; i <= 7; i++) {
        data[`date_d${i}`] = m.isoWeekday() === i ? 'x' : ' '
    }
}

function populateDerivedDateFields(data, fieldName) {
    const m = moment(data[fieldName], 'DD/MM/YYYY HH:mm:ss')
    data[`${fieldName}_day`] = m.format('DD/MM/YYYY')
    data[`${fieldName}_time`] = m.format('H:mm')
    for (let i = 1; i <= 7; i++) {
        data[`${fieldName}_d${i}`] = m.isoWeekday() === i ? 'x' : ' '
    }
}

function populatePrintDateFields(data, context, nextState, channel) {
    data.print_date = moment(getCurrentTime(context)).format('DD/MM/YYYY HH:mm:ss')
    data.print_date_timestamp = moment(data.print_date, 'DD/MM/YYYY HH:mm:ss').valueOf() / 1000
    populateDerivedDateFields(data, 'print_date')
    data.state = context.getters.getStates.filter((state) => {
        return state.id == nextState || (channel && state.id == nextState + '_channel' + channel)
    })[0].name
    data.currentday = moment().format('DD/MM/YYYY 00:00')
    data.currentday_timestamp = moment().format('DD/MM/YYYY 00:00').valueOf()
    data.currenttime = data.print_date
    data.currenttime_timestamp = data.print_date_timestamp
}

function populateWeekdayFields(data) {
    let dateParams = ['print_date', 'currentday', 'currenttime', 'date', 'prep_date']
    dateParams.forEach((date) => {
        for (let i = 0; i < 4; i++) {
            let aux = moment(data[date], 'DD/MM/YYYY HH:mm:ss')
            if (aux.isValid()) {
                let day = i18n.t('calendar.days')[aux.day()].toUpperCase()
                if (i > 0) {
                    data[date + '_wd' + i] = day.slice(0, i)
                } else {
                    data[date + '_wd'] = day
                }
            } else {
                let day = i18n.t('calendar.days')[moment().day()].toUpperCase()
                if (i > 0) {
                    data[date + '_wd' + i] = day.slice(0, i)
                } else {
                    data[date + '_wd'] = day
                }
            }
        }
    })
}

function parseNextStateChannel(nextStateParam, timer) {
    let nextState = String(nextStateParam)
    let channel = false
    let nextChannelNum = null
    if (nextState.includes('_channel')) {
        channel = nextState.split('_channel')[1]
        nextState = nextState.split('_channel')[0]
        nextChannelNum = parseInt(channel, 10)
        timer.data.channel = channel
    }
    return { nextState, channel, nextChannelNum }
}

/**
 * Persiste un timer offline con id y clave de cola únicos (BUILD-7488 altas masivas).
 *
 * @param {Object} context
 * @param {Object} params
 * @returns {Promise<void>}
 */
async function queueOfflineTimer(context, params) {
    const identity = nextOfflineTimerIdentity()
    params.id = identity.id
    params.reference_id = params.product
    params.data = typeof params.data === 'string' ? JSON.parse(params.data) : params.data
    params.status = 1
    context.commit('setTimer', params)
    context.dispatch('updateCategories')
    const currentTime = getCurrentTime(context)
    params.current_print_time = currentTime
    await db_timerlogs.setItem(identity.queueKey, params)
}

function getBackwardMoveParentId(timer) {
    if (!timer.data.timers_relations) {
        return null
    }
    try {
        const timersRelations = JSON.parse(timer.data.timers_relations)
        if (timersRelations.length > 0) {
            return timersRelations[0].id_timer_rel
        }
    } catch (error) {
        logError('Error parsing timers_relations:', error)
    }
    return null
}

async function updateSourceQuantityAfterBackwardCreate(context, timer) {
    const newQuantityAvaibles = Number(timer.data.quantity_avaibles) - Number(timer.quantity_move)
    await context.dispatch('updateTimer', {
        id: timer.id,
        data: { quantity_avaibles: newQuantityAvaibles }
    })
}

async function handleBackwardMoveNoTimeLimit(context, timer, lastState, nextStateData, nextState, parentId, parentExists, allTimers, productRefBackward) {
    if (timer.quantity === 1) {
        await context.dispatch('createNewTimerNotParent', {
            timer,
            lastState,
            nextStateData,
            nextState
        })
        context.commit('deleteTimer', timer.id)
        context.dispatch('deleteTimers', { timers: [timer] })
    } else {
        const newQuantityAvaibles = Number(timer.data.quantity_avaibles) - Number(timer.quantity_move)
        await context.dispatch('updateTimer', {
            id: timer.id,
            data: { quantity_avaibles: newQuantityAvaibles }
        })

        if (parentExists && allTimers[parentId]) {
            const newQuantityAvaiblesParent = Number(allTimers[parentId].data.quantity_avaibles) + Number(timer.quantity_move)
            const todasLasUnidadesAlPadre = newQuantityAvaibles === 0 || timer.data.quantity_avaibles === 'NaN'

            await context.dispatch('updateTimer', {
                id: parentId,
                data: {
                    quantity_avaibles: newQuantityAvaiblesParent,
                    ...buildCaducidadSnapshotsForParentFromChild(productRefBackward, timer.data, todasLasUnidadesAlPadre)
                }
            })
        } else {
            await context.dispatch('createNewTimerNotParent', {
                timer,
                lastState,
                nextStateData,
                nextState
            })
        }
    }
    return true
}

async function handleBackwardMoveWithinLimit(context, timer, lastState, nextStateData, nextState, parentExists, now, data, date) {
    timer.data.last_move_timestamp = now

    if (!parentExists) {
        await context.dispatch('createNewTimerNotParent', {
            timer,
            lastState,
            nextStateData,
            nextState
        })

        if (timer.quantity === 1) {
            context.commit('deleteTimer', timer.id)
            context.dispatch('deleteTimers', { timers: [timer] })
        } else {
            await updateSourceQuantityAfterBackwardCreate(context, timer)
        }
        return true
    }

    const productRefBackward = context.getters.getProducts(timer.reference_id)
    if (referenceCanMovePast(productRefBackward) && data.caducidad_estado_inicial_timestamp) {
        applyCaducidadFromUnix(data, data.caducidad_estado_inicial_timestamp)
        date = moment(data.date_timestamp * 1000)
    }

    data.date_timestamp = data.date_timestamp
    populateDerivedDateFields(data, 'date')
    data.prep_date_timestamp = data.prep_date_timestamp
    populateDerivedDateFields(data, 'prep_date')
    return { date }
}

async function handleBackwardMoveExceeded(context, timer, lastState, nextStateData, nextState) {
    await context.dispatch('createNewTimerNotParent', {
        timer,
        lastState,
        nextStateData,
        nextState
    })

    if (timer.quantity === 1) {
        context.commit('deleteTimer', timer.id)
        context.dispatch('deleteTimers', { timers: [timer] })
    } else {
        await updateSourceQuantityAfterBackwardCreate(context, timer)
    }
    return true
}

function populateForwardPrepDateFields(data, nextStateData, date) {
    data.prep_date = moment(data.print_date, 'DD/MM/YYYY HH:mm:ss').add(nextStateData.preptime, 'minutes').format('DD/MM/YYYY HH:mm:ss')
    if (moment(data.prep_date, 'DD/MM/YYYY HH:mm:ss').isAfter(date)) {
        // TODO SHOW ERROR CAN NOT GENERATE TIMER
    }
    data.prep_date_timestamp = moment(data.prep_date, 'DD/MM/YYYY HH:mm:ss').valueOf() / 1000
    populateDerivedDateFields(data, 'prep_date')
}

function handleForwardMove(data, nextStateData, currentStateData, productRef, date) {
    const { minOrder, maxOrder } = getMinMaxStateOrder(productRef.states)
    const lastOrderNum = parseInt(currentStateData.order, 10)
    const nextOrderNum = parseInt(nextStateData.order, 10)
    const isForwardMove = nextOrderNum > lastOrderNum
    const useUltimoCaducidad = isForwardMove && referenceCanMovePast(productRef) && nextOrderNum === maxOrder && data.caducidad_ultimo_estado_timestamp != null && data.caducidad_ultimo_estado_timestamp !== '' && !nextStateData.msg

    if (nextStateData.msg) {
        data.date = nextStateData.msg
    } else if (useUltimoCaducidad) {
        applyCaducidadFromUnix(data, data.caducidad_ultimo_estado_timestamp)
        date = moment(data.date_timestamp * 1000)
        populateForwardPrepDateFields(data, nextStateData, date)
    } else {
        data.date = moment(data.print_date, 'DD/MM/YYYY HH:mm:ss').add(nextStateData.time, 'minutes').format('DD/MM/YYYY HH:mm:ss')

        const isAfterOriginal = moment(data.date, 'DD/MM/YYYY HH:mm:ss').isAfter(date)
        if (isAfterOriginal) {
            data.date = date.format('DD/MM/YYYY HH:mm:ss')
        }
        data.date_timestamp = moment(data.date, 'DD/MM/YYYY HH:mm:ss').valueOf() / 1000
        populateDerivedDateFields(data, 'date')
        populateForwardPrepDateFields(data, nextStateData, date)
    }

    if (referenceCanMovePast(productRef) && isForwardMove && data.date_timestamp) {
        if (lastOrderNum === minOrder && !data.caducidad_estado_inicial_timestamp) {
            data.caducidad_estado_inicial_timestamp = date.unix()
        }
        if (nextOrderNum === maxOrder) {
            data.caducidad_ultimo_estado_timestamp = data.date_timestamp
        }
    }

    return date
}

async function persistMovedTimer(context, timer, nextState, data, params, { preserveTimerDates = false, originalDateSnapshot = null } = {}) {
    if (fnCheckConnection()) {
        let info = {
            state: nextState,
            reference_id: timer.reference_id,
            data: JSON.stringify(data)
        }

        return api.post('timerproduct/timers/' + timer.id + '/move', info).then(async function (response) {
            if (response && response.status) {
                const newTimerId = response.data.id

                if (!preserveTimerDates && response.data && response.data.data) {
                    try {
                        const serverData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data
                        if (serverData && typeof serverData === 'object') {
                            Object.assign(data, serverData)
                        }
                    } catch (error) {
                        logError('Error parsing move response data:', error)
                    }
                }

                if (preserveTimerDates && originalDateSnapshot) {
                    applyTimerDateSnapshot(data, originalDateSnapshot)
                    syncTimerDateStringsFromTimestamps(data)
                }

                if (timer.quantity_move != null) {
                    data.quantity = timer.quantity_move
                    data.quantity_avaibles = timer.quantity_move
                }

                timer.data = data
                timer.id = newTimerId
                timer.status = 1
                context.commit('setTimer', timer)
                context.dispatch('updateCategories')
                return response.data.id
            } else {
                params.id = 'tp_' + moment().valueOf()
                params.reference_id = params.product
                params.data = JSON.parse(params.data)
                params.status = 1
                context.commit('setTimer', params)
                const currentTime = getCurrentTime(context)
                params.current_print_time = currentTime
                await db_timerlogs.setItem(moment().valueOf(), params)
                context.dispatch('updateCategories')
                return false
            }
        })
    } else {
        params.id = 'tp_' + moment().valueOf()
        params.reference_id = params.product
        params.data = JSON.parse(params.data)
        params.status = 1
        context.commit('setTimer', params)
        context.dispatch('updateCategories')

        const currentTime = getCurrentTime(context)
        params.current_print_time = currentTime
        await db_timerlogs.setItem(moment().valueOf(), params)
    }
}

const getDefaultState = () => {
    return {
        categories: {},
        states: [],
        timers: [],
        interval: null,
        intervalMs: TIMER_TICK_SLOW_MS,
        products: {},
        productsOrdered: {},
        deletedTimers: {},
        /** Filtro KPI activo del listado: 'with_timer' | 'without_timer' | null */
        activeKpiFilter: null,
        /** BUILD-7490: “Ahora no” solo dura la visita actual a la pantalla. */
        recommendationDismissed: false
    }
}
// initial state
const state = getDefaultState()

const getters = {
    getStates(state, getters, rootState) {
        return state.states
    },
    getCategories(state, getters, rootState) {
        return state.categories
    },
    getSelectedCategory: (state, getters, rootState) => {
        for (var i in state.categories) {
            if (state.categories[i].selected) return state.categories[i]
        }
        return false
    },
    getSortedCategories: (state, getters, rootState) => {
        return Object.values(state.categories).sort((a, b) => parseInt(a.order) - parseInt(b.order))
    },
    getTimers(state, getters, rootState) {
        return state.timers
    },
    getTimersDeletes(state, getters, rootState) {
        return state.deletedTimers
    },
    getTimersByCategory: (state, getters, rootState) => (categoryId) => {
        var timers = {}

        var columns = {}
        for (var idx in state.states) {
            timers[state.states[idx].id] = []
        }

        for (var idx in state.timers) {
            var timer = state.timers[idx]
            if (!categoryId || (categoryId && categoryId == state.products[timer.reference_id].category_id)) {
                if (timer.status == TIMERPRODUCT_STATUS.EXPIRED) {
                    timers[false].push(timer)
                } else {
                    if (!timers[timer.state]) {
                        let channel = timer.data.channel ? timer.data.channel : 1
                        timers[timer.state + '_channel' + channel].push(timer)
                    } else {
                        timers[timer.state].push(timer)
                    }
                }
            }
        }
        return timers
    },
    getTimersFilterByCategory: (state, getters, rootState) => (categoryId) => {
        const timersSelect = getters.getTimersByCategory()

        return timersSelect[categoryId]
    },
    getTimersFilterByCategorySelected: (state, getters, rootState) => (categoryId) => {
        const timersSelect = getters.getTimersByCategory()
        const timersCategory = timersSelect[categoryId]
        const filteredTimers = timersCategory.filter((timer) => timer.isSelected === true)
        return filteredTimers
    },
    getFilteredTimers: (state, getters, rootState) => (categoryId, stateId) => {
        var timers = []

        for (var idx in state.timers) {
            var timer = state.timers[idx]
            if (!categoryId || (categoryId && categoryId == timer.category)) {
                timers[timer.state].push(timer)
            }
        }
        return timers
    },
    getLabelStates: (state, getters, rootState) => (id) => {
        var states = {}
        for (var i in state.products) {
            var product = state.products[i]
            for (var j in product.states) {
                if (!Object.keys(states)[product.states[j].id]) {
                    states[product.states[j].id] = product.states[j]
                }
            }
        }
        if (typeof id !== 'undefined') return states[id]
        return states
    },
    getProducts: (state, getters, rootState) => (id) => {
        return id ? (state.products[id] ? state.products[id] : false) : state.products
    },
    getProductsOrdered: (state, getters, rootState) => {
        return state.productsOrdered
    },
    getHomeWidgetData: (state, getters, rootState) => {
        var data = {
            actives: 0,
            to_delete: 0,
            without_timer: 0,
            close_to_expire: 0
        }

        for (var idx in state.timers) {
            var timer = state.timers[idx]
            if (timer.status == TIMERPRODUCT_STATUS.EXPIRED) {
                data.to_delete += 1
            } else if (timer.status == TIMERPRODUCT_STATUS.CLOSE_TO_EXPIRE) {
                data.close_to_expire += 1
            } else {
                data.actives += 1
            }
        }

        for (var idp in state.products) {
            var product = state.products[idp]
            if (!Object.values(state.timers).filter((timer) => timer.reference_id == product.id).length) {
                data.without_timer += 1
            }
        }

        return data
    },
    /**
     * Mapa reactivo de productos con al menos un timer (BUILD-7489).
     * Objeto plano (no Set) para invalidación correcta en Vue 2/Vuex al mutar timers.
     * Misma regla que getHomeWidgetData.without_timer: `timer.reference_id == product.id`.
     */
    getProductIdsWithTimer: (state) => {
        const productIdsWithTimer = Object.create(null)
        const timers = Object.values(state.timers)

        for (let i = 0; i < timers.length; i++) {
            const timer = timers[i]
            if (timer && timer.reference_id != null && timer.reference_id !== '') {
                productIdsWithTimer[String(timer.reference_id)] = true
            }
        }

        return productIdsWithTimer
    },
    /**
     * Contadores KPI derivados del estado local (reactivos vía getters Vuex).
     * Se recalculan solos al crear/mover/eliminar timers, al cambiar el catálogo
     * o al cambiar la categoría seleccionada (misma regla que el listado).
     */
    getProductTimerKpiCounts: (state, getters) => {
        const productIdsWithTimer = getters.getProductIdsWithTimer
        const selectedCategory = getters.getSelectedCategory
        const selectedCategoryId = selectedCategory && selectedCategory.id !== false && selectedCategory.id !== 'false' ? selectedCategory.id : null
        const products = Object.values(state.products)
        let withTimer = 0
        let total = 0

        for (let j = 0; j < products.length; j++) {
            const product = products[j]
            if (!product) {
                continue
            }
            if (selectedCategoryId != null && product.category_id != selectedCategoryId) {
                continue
            }
            total += 1
            if (productIdsWithTimer[String(product.id)]) {
                withTimer += 1
            }
        }

        return {
            total,
            with_timer: withTimer,
            without_timer: total - withTimer
        }
    },
    getActiveKpiFilter: (state) => {
        return state.activeKpiFilter
    },
    getTimersByStateAndProduct: (state, getters, rootState) => (productId) => {
        let states = []
        state.states.forEach((state) => {
            states.push({ id: state.id, status: -1 })
        })
        let timers = Object.values(state.timers).filter((timer) => {
            return timer.reference_id == productId
        })
        timers.forEach((timer) => {
            if (timer.status == TIMERPRODUCT_STATUS.EXPIRED) {
                states[states.length - 1].status = TIMERPRODUCT_STATUS.EXPIRED
            } else {
                states.forEach((state) => {
                    if ((timer.state == state.id || state.id == timer.state + '_channel' + (timer.data.channel ? timer.data.channel : '1')) && timer.status > state.status) {
                        state.status = timer.status
                    }
                })
            }
        })

        return states
    },

    // === GETTER PARA AGRUPACIÓN DE TIMERS POR CATEGORÍA/ESTADO ===
    // Agrupa automáticamente los timers por: Producto + Estado + Caducidad
    // Organiza los grupos por columna/estado, compatible con Column.vue

    /**
     * Agrupa los timers por producto, estado y caducidad, organizados por columna/estado
     * Compatible con el componente Column.vue
     *
     * @param {string|boolean} categoryId - ID de categoría para filtrar (false = todas)
     * @returns {Object} Objeto con claves = IDs de estado y valores = arrays de grupos
     *
     * Campos adicionales de agrupación en cada grupo:
     * - quantity: Cantidad total de timers en el grupo
     * - groupedTimers: Array con los timers individuales del grupo
     * - groupKey: Clave única del grupo (producto_estado_caducidad)
     */
    getTimersGroupedByCategory: (state, getters) => (categoryId) => {
        const timers = state.timers
        const products = state.products
        const states = state.states

        // Inicializar objeto de resultado con todos los estados
        const result = {}
        for (const idx in states) {
            result[states[idx].id] = []
        }

        if (!timers || Object.keys(timers).length === 0) {
            return result
        }

        // Crear grupos por producto + estado + caducidad
        const groups = {}

        Object.values(timers).forEach((timer) => {
            // Filtrar por categoría si se especifica
            if (categoryId && products[timer.reference_id] && categoryId !== products[timer.reference_id].category_id) {
                return
            }

            // Validar que el producto existe
            if (!products[timer.reference_id] || !products[timer.reference_id].states[timer.state]) {
                return
            }

            const productId = timer.reference_id || 'unknown'
            const timerState = timer.state || 'unknown'
            const timerChannel = timer.data && timer.data.channel ? timer.data.channel : 1
            const expirationTimestamp = timer.data && timer.data.date_timestamp ? timer.data.date_timestamp : 0
            // Incluir el canal en la clave para evitar agrupar timers de diferentes mesas/canales
            const groupKey = productId + '_' + timerState + '_' + timerChannel + '_' + expirationTimestamp

            if (!groups[groupKey]) {
                groups[groupKey] = {
                    ...timer,
                    groupKey,
                    quantity: 0,
                    groupedTimers: []
                }
            }

            groups[groupKey].groupedTimers.push(timer)
            groups[groupKey].quantity += 1

            // Actualizar el estado del grupo al peor estado de sus timers
            if (timer.status === TIMERPRODUCT_STATUS.EXPIRED) {
                groups[groupKey].status = TIMERPRODUCT_STATUS.EXPIRED
            } else if (timer.status === TIMERPRODUCT_STATUS.CLOSE_TO_EXPIRE && groups[groupKey].status !== TIMERPRODUCT_STATUS.EXPIRED) {
                groups[groupKey].status = TIMERPRODUCT_STATUS.CLOSE_TO_EXPIRE
            }
        })

        // Organizar grupos por estado/columna
        Object.values(groups).forEach((group) => {
            if (group.status === TIMERPRODUCT_STATUS.EXPIRED) {
                // Los expirados van a la columna "Desechar" (id: false)
                if (result[false]) {
                    result[false].push(group)
                }
            } else {
                // Determinar la columna correcta (considerando canales)
                const channel = group.data && group.data.channel ? group.data.channel : 1
                const columnWithChannel = group.state + '_channel' + channel

                if (result[columnWithChannel]) {
                    result[columnWithChannel].push(group)
                } else if (result[group.state]) {
                    result[group.state].push(group)
                }
            }
        })

        // Ordenar cada columna por caducidad (vencidos primero, luego por timestamp)
        Object.keys(result).forEach((stateId) => {
            result[stateId].sort((a, b) => {
                const aExpired = a.status === TIMERPRODUCT_STATUS.EXPIRED
                const bExpired = b.status === TIMERPRODUCT_STATUS.EXPIRED
                if (aExpired && !bExpired) return -1
                if (!aExpired && bExpired) return 1

                const aTimestamp = a.data && a.data.date_timestamp ? a.data.date_timestamp : 0
                const bTimestamp = b.data && b.data.date_timestamp ? b.data.date_timestamp : 0
                return aTimestamp - bTimestamp
            })
        })

        return result
    },
    /**
     * BUILD-7500: KPIs locales de Radar para Timers de producto.
     * - product_timers_expired → status EXPIRED (rojo)
     * - product_timers_expiring_soon → status CLOSE_TO_EXPIRE (amarillo)
     * Misma fuente que el widget de Inicio (`getHomeWidgetData`).
     * Al leer los counts en el getter externo, Radar se recalcula al vencer/cambiar timers.
     */
    getRadarModuleState: (state, getters) => {
        const widgetData = getters.getHomeWidgetData || {}
        const expiredCount = Number(widgetData.to_delete) || 0
        const expiringSoonCount = Number(widgetData.close_to_expire) || 0

        return (kpiKeys) => {
            const localKpiCounts = buildDefaultLocalKpiCounts(TOOLS.PRODUCT_TIMERS, kpiKeys)

            if ('product_timers_expired' in localKpiCounts) {
                localKpiCounts.product_timers_expired = expiredCount
            }

            if ('product_timers_expiring_soon' in localKpiCounts) {
                localKpiCounts.product_timers_expiring_soon = expiringSoonCount
            }

            // Shape consumido por Radar (solo valores dinámicos; metadata viene del schema):
            // {
            //   kpis: {
            //     product_timers_expired:       { count: number, severity: 'critical' },
            //     product_timers_expiring_soon:  { count: number, severity: 'warning' }
            //   }
            // }
            return buildRadarModuleState(TOOLS.PRODUCT_TIMERS, null, kpiKeys, localKpiCounts)
        }
    },
    /**
     * Evaluación reactiva de triggers de Andy (BUILD-7490 / BUILD-7493).
     * ADD/COMBINED exigen ficha de catálogo con states para poder abrir el wizard.
     */
    getRecommendationEvaluation: (state) => {
        return evaluateRecommendationTriggers(state.timers, undefined, state.products)
    },
    /**
     * Variante visible: una sola, o vacía si el usuario pulsó “Ahora no”.
     */
    getActiveRecommendationVariant: (state, getters) => {
        if (state.recommendationDismissed) {
            return ''
        }

        return getters.getRecommendationEvaluation.variant
    },
    /**
     * BUILD-7493: productos naranja sin backup, listos para el wizard.
     * Misma fuente que el trigger ADD (catálogo con `states`).
     */
    getRecommendedAddProducts: (state) => {
        return collectRestockProducts(state.timers, state.products)
    }
}

/**
 * Helper function para extraer el ID de un timer (puede ser objeto o ID directo)
 * @param {object|string|null|undefined} timer - Timer como objeto con propiedad id o como ID directo
 * @returns {string|null} - ID del timer o null si el timer es null/undefined/inválido
 */
function getTimerId(timer) {
    // Manejar null y undefined (typeof null === 'object' en JavaScript)
    if (timer === null || timer === undefined) {
        return null
    }
    // Si es un objeto con propiedad id, retornar el id
    if (typeof timer === 'object' && timer.id) {
        return timer.id
    }
    // Si es un string o número, retornarlo directamente
    if (typeof timer === 'string' || typeof timer === 'number') {
        return timer
    }
    // Caso por defecto: retornar null para valores inválidos
    return null
}

/** Mismo tamaño de lote que el borrado masivo del board. */
const DELETE_EXPIRED_CHUNK_SIZE = 20

/**
 * Parte una lista en lotes para no saturar `timerproduct/timers/removes`.
 *
 * @param {Array} items
 * @param {number} size
 * @returns {Array<Array>}
 */
function chunkTimerList(items, size) {
    if (!Array.isArray(items) || items.length === 0) {
        return []
    }

    const parsedSize = Number(size)
    const chunkSize = Number.isFinite(parsedSize) && parsedSize > 0 ? parsedSize : DELETE_EXPIRED_CHUNK_SIZE
    const chunks = []

    for (let index = 0; index < items.length; index += chunkSize) {
        chunks.push(items.slice(index, index + chunkSize))
    }

    return chunks
}

const actions = {
    loadSchema(context, params) {
        // TODO - API request
        var columns = [
            { id: '1', name: 'Cámara', qty: 3, color: 'ok' },
            { id: '2', name: 'Mesa', qty: 3, color: 'ok' },
            { id: false, name: 'Desechar', qty: 2, color: 'error' }
        ]
        // var categories = {
        //     false: { id: false, name: 'Todos' },
        //     '1': { id: '1', name: 'Salsas' },
        //     '2': { id: '2', name: 'Vegetales' },
        //     '3': { id: '3', name: 'Otros' },
        //     '4': { id: '4', name: 'Postres' },
        //     '5': { id: '5', name: 'Carne' }
        // }

        return context.commit('setStates', columns)
        // return context.commit('setCategories', categories)
    },
    /**
     * Sincroniza timers de producto con la API y recalcula status (EXPIRED / CLOSE_TO_EXPIRE vía processTimers).
     * @returns {Promise<boolean>} true solo si la carga y el recálculo terminaron bien (BUILD-7500 / Radar)
     */
    loadTimers(context, params) {
        // db_timerlogs es cola de ops offline (a menudo vacía tras uploadTimerOffline), no caché de timers.
        // Tras soft-fail/error de red, solo se usa si no hay timers válidos ya en memoria.
        const restoreTimersOfflineIfEmpty = () => {
            const hasTimersInMemory = state.timers && Object.keys(state.timers).length > 0

            if (hasTimersInMemory) {
                return Promise.resolve(false)
            }

            return context
                .dispatch('loadTimersOffline')
                .then(() => false)
                .catch(() => false)
        }

        if (fnCheckConnection()) {
            return context.dispatch('uploadTimerOffline').then(() => {
                return api
                    .get('timerproducts/timers', {})
                    .then(function (response) {
                        // Api.get soft-fail resuelve { status: false, msg } (no reject).
                        if (!response || response.status === false || response.data == null) {
                            logError((response && response.msg) || 'Empty timerproducts/timers response')

                            return restoreTimersOfflineIfEmpty()
                        }

                        var timers = response.data
                        for (var key in timers) {
                            timers[key].status = TIMERPRODUCT_STATUS.AVAILABLE
                        }

                        context.commit('setTimers', timers)
                        // setupInterval → processTimers recalcula EXPIRED / CLOSE_TO_EXPIRE antes de resolver
                        return context
                            .dispatch('updateCategories')
                            .then(() => {
                                return context.dispatch('setupInterval', { intervalMs: state.intervalMs })
                            })
                            .then(() => true)
                    })
                    .catch((error) => {
                        logError(error)

                        return restoreTimersOfflineIfEmpty()
                    })
            })
        } else if (context.rootGetters['getRefresh'].products) {
            return context
                .dispatch('loadTimersOffline')
                .then(() => true)
                .catch(() => false)
        }

        return Promise.resolve(false)
    },

    uploadTimerOffline(context) {
        const entries = []
        return db_timerlogs
            .iterate(function (value, key) {
                entries.push({ key, value })
            })
            .then(async () => {
                for (let i = 0; i < entries.length; i++) {
                    const { key, value } = entries[i]
                    const row = Object.assign({}, value)
                    const isQueuedUpdate = row.timerproduct_offline_update === true
                    delete row.timerproduct_offline_merged_data
                    delete row.timerproduct_offline_update

                    if (isQueuedUpdate && row.id) {
                        const patch = typeof row.data === 'object' && row.data !== null ? row.data : JSON.parse(row.data || '{}')
                        try {
                            const response = await api.post('timerproduct/timers/' + row.id + '/update', patch)
                            if (response && response.status) {
                                context.commit('updateTimer', { id: row.id, data: patch })
                                await db_timerlogs.removeItem(key)
                            }
                        } catch (e) {
                            logError(e)
                        }
                        continue
                    }

                    row.data = typeof row.data === 'string' ? row.data : JSON.stringify(row.data || {})
                    await context.dispatch('addTimer', row)
                    await db_timerlogs.removeItem(key)
                }
            })
    },

    loadTimersOffline(context) {
        let timers = {}
        return db_timerlogs
            .iterate(function (value, key, iterationNumber) {
                let row = value
                if (row && row.timerproduct_offline_merged_data) {
                    row = Object.assign({}, row, {
                        data: row.timerproduct_offline_merged_data
                    })
                    delete row.timerproduct_offline_merged_data
                }
                if (row && row.timerproduct_offline_update !== undefined) {
                    delete row.timerproduct_offline_update
                }
                timers[key] = row
            })
            .then(() => {
                context.commit('setTimers', timers)
                return context.dispatch('setupInterval', { intervalMs: state.intervalMs })
            })
    },
    setupInterval(context, params) {
        const ms = params && params.intervalMs != null ? params.intervalMs : state.intervalMs != null ? state.intervalMs : TIMER_TICK_SLOW_MS

        if (ms === state.intervalMs && state.interval) {
            return context.dispatch('processTimers')
        }

        if (state.interval) {
            clearInterval(state.interval)
        }

        state.intervalMs = ms

        const interval = setInterval(function () {
            return context.dispatch('processTimers')
        }, ms)

        context.commit('setInterval', interval)

        return context.dispatch('processTimers')
    },
    processTimers(context) {
        var products = {}
        var updated = false
        for (var idx in state.timers) {
            var timer = state.timers[idx]

            if (!state.products[timer.reference_id] || !state.products[timer.reference_id].states[timer.state]) {
                delete state.timers[idx]
                continue
            }

            if (timer.status > 0) {
                // Only process active timers
                var now = moment()

                // Timer prep date
                // si existe el timer y (existe el prep_date o el prep_date_timestamp) y el status es mayor o igual a 1
                if (timer && (timer.prep_date || timer.data.prep_date_timestamp) && timer.status >= TIMERPRODUCT_STATUS.PREPARATION) {
                    let print_date = timer.print_date ? timer.print_date : timer.data.print_date_timestamp
                    let prep_date = timer.prep_date ? timer.prep_date : timer.data.prep_date_timestamp
                    if (print_date != prep_date) {
                        var duration = moment.duration(moment(prep_date * 1000).diff(now))
                        var diff = duration.asSeconds()

                        if (diff <= 0) {
                            updated = timer.status != TIMERPRODUCT_STATUS.AVAILABLE
                            timer.status = TIMERPRODUCT_STATUS.AVAILABLE
                        } else if (diff > 0 && diff <= 10 * 60) {
                            updated = timer.status != TIMERPRODUCT_STATUS.CLOSE_TO_EXPIRE
                            timer.status = TIMERPRODUCT_STATUS.CLOSE_TO_EXPIRE
                        } else {
                            updated = timer.status != TIMERPRODUCT_STATUS.PREPARATION
                            timer.status = TIMERPRODUCT_STATUS.PREPARATION
                        }
                    } else {
                        if (diff > 0 && diff <= 10 * 60) {
                            updated = timer.status != TIMERPRODUCT_STATUS.CLOSE_TO_EXPIRE
                            timer.status = TIMERPRODUCT_STATUS.CLOSE_TO_EXPIRE
                        } else {
                            updated = timer.status != TIMERPRODUCT_STATUS.AVAILABLE
                            timer.status = TIMERPRODUCT_STATUS.AVAILABLE
                        }
                    }
                } else if (timer.status == TIMERPRODUCT_STATUS.PREPARATION) {
                    // si el timer status es 1
                    if (dif > 0 && diff <= 10 * 60) {
                        updated = timer.status != TIMERPRODUCT_STATUS.CLOSE_TO_EXPIRE
                        timer.status = TIMERPRODUCT_STATUS.CLOSE_TO_EXPIRE
                    } else {
                        updated = timer.status != TIMERPRODUCT_STATUS.AVAILABLE
                        timer.status = TIMERPRODUCT_STATUS.AVAILABLE
                    }
                }

                // Timer expiration date
                var duration = moment.duration(moment(timer.data.date_timestamp * 1000).diff(now))
                var diff = duration.asSeconds()

                if (diff <= 0) {
                    updated = timer.status != TIMERPRODUCT_STATUS.EXPIRED
                    timer.status = TIMERPRODUCT_STATUS.EXPIRED
                    // timer.state = false
                } else if (diff <= 10 * 60 || (state.products[timer.reference_id] && state.products[timer.reference_id].states[timer.state] && state.products[timer.reference_id].states[timer.state].preptime && diff < state.products[timer.reference_id].states[timer.state].preptime * 60)) {
                    // Timer is less than 10min of expiration OR less than prep time expiration
                    updated = timer.status != TIMERPRODUCT_STATUS.CLOSE_TO_EXPIRE
                    timer.status = TIMERPRODUCT_STATUS.CLOSE_TO_EXPIRE
                }
            }

            // Update timer status of related product
            if (timer && typeof products[timer.reference_id] === 'undefined') {
                products[timer.reference_id] = []
            }

            if (timer && products[timer.reference_id].length > 0) {
                const leaderStatus = products[timer.reference_id][0].status
                if (timer.same_product_status !== leaderStatus) {
                    timer.same_product_status = leaderStatus
                    updated = true
                }

                for (var relIdx in products[timer.reference_id]) {
                    const relatedTimer = products[timer.reference_id][relIdx]
                    if (state.timers[relatedTimer.id] && state.timers[relatedTimer.id].same_product_status !== timer.status) {
                        state.timers[relatedTimer.id].same_product_status = timer.status
                        updated = true
                    }
                }
            }
            if (timer) {
                products[timer.reference_id].push(timer)
            }
        }
        if (updated) {
            context.dispatch('updateCategories')
            return context.commit('setTimers', { ...state.timers })
        }
    },
    setActiveKpiFilter(context, filter) {
        const validFilters = ['with_timer', 'without_timer']
        const nextFilter = filter && validFilters.includes(filter) ? filter : null
        context.commit('setActiveKpiFilter', nextFilter)
    },
    selectCategory(context, categoryId) {
        var categories = state.categories

        // Verificar que existen categorías cargadas
        if (!categories || Object.keys(categories).length === 0) {
            return Promise.resolve()
        }

        // Deseleccionar todas las categorías
        Object.keys(categories).forEach((key) => (categories[key].selected = false))

        // Seleccionar la categoría solicitada solo si existe
        if (categoryId !== null && categoryId !== undefined && categories[categoryId]) {
            categories[categoryId].selected = true
        }

        return context.commit('setCategories', { ...categories })
    },
    updateCategories(context) {
        var categories = state.categories
        var timers = state.timers
        var products = state.products
        Object.keys(categories).forEach((key) => (categories[key].qty = 0))

        for (var idx in timers) {
            var timer = timers[idx]
            timer.isLoading = false
            if (timer.status == TIMERPRODUCT_STATUS.EXPIRED && categories[products[timer.reference_id]]) {
                categories[products[timer.reference_id].category_id].qty += 1
                categories[false].qty += 1
            }
        }

        return context.commit('setCategories', { ...categories })
    },
    loadTimerProducts(context, params) {
        if (context.rootGetters['getRefresh'].timerproducts && fnCheckConnection()) {
            return api
                .get('timerproducts', {})
                .then(async function (response) {
                    var data = format(context, response.data.categories)
                    var dataDefault = format(context, response.data.default)
                    context.commit('setCategories', data.categories)
                    context.commit('setStates', data.states)
                    context.commit('setProducts', data.products)
                    context.commit('setRefreshTimerProducts', false, { root: true })
                    // context.commit('setPredefined', dataDefault.categories);

                    await db_tpcategories.clear()
                    await db_timerproducts.clear()
                    await db_tpstates.clear()

                    _.forEach(_.values(data.categories), async function (value, key) {
                        await db_tpcategories.setItem(value.id, value)
                    })

                    _.forEach(_.values(data.states), async function (value, key) {
                        await db_tpstates.setItem(value.id, value)
                    })

                    _.forEach(_.values(data.products), async function (value, key) {
                        await db_timerproducts.setItem(value.id, value)
                    })

                    if (context.rootGetters.getIsApp && parseInt(response.data.update) == 1) {
                        context.dispatch('processLabelsImage', data.products)
                    }

                    context.rootState.refresh.timerproducts = false
                })
                .catch((error) => {
                    logError(error)
                    // loadLabelsOffline(context)
                })
        } else if (context.rootGetters['getRefresh'].products) {
            // loadLabelsOffline(context)
        }
    },

    processLabelsImage(context, params) {
        var aImages = []
        var aLocalImages = []
        for (var k in params) {
            var label = params[k]
            if (label.image && label.image != 'false' && label.image != 'null') {
                if (!context.getters['getImagesDownloaded'] || (label.last_update && context.getters['getImagesDownloaded'] < moment(label.last_update).valueOf())) {
                    aImages.push(label)
                } else {
                    aLocalImages.push(label)
                }
            }
        }

        return context.dispatch('queueDownloadImages', aImages).then(() => {
            return context.dispatch('queueLocalImages', aLocalImages)
        })
    },

    queueLocalImages(context, queue) {
        return new Promise((resolve, reject) => {
            if (queue.length > 0) {
                var label = Object.assign({}, queue.pop())
                var imageName = label.image.split('/')
                imageName = imageName[imageName.length - 1]

                window.resolveLocalFileSystemURL(
                    context.rootGetters.getPathFiles + 'images/' + imageName,
                    function (fileEntry) {
                        fileEntry.file(
                            async function (entry) {
                                label.local_image = context.rootGetters.getPathFiles + 'images/' + imageName
                                await db_timerproducts.setItem(label.id, label)
                                context.commit('setLabel', label)
                                return context.dispatch('queueLocalImages', queue)
                            },
                            function (e) {
                                return context.dispatch('queueLocalImages', queue)
                            }
                        )
                    },
                    function (e) {
                        return context.dispatch('queueLocalImages', queue)
                    }
                )
            } else {
                resolve()
            }
        })
    },

    queueDownloadImages(context, queue) {
        return new Promise((resolve, reject) => {
            if (queue.length > 0) {
                var label = queue.pop()
                var imageName = label.image.split('/')
                imageName = imageName[imageName.length - 1]
                window.resolveLocalFileSystemURL(
                    context.rootGetters.getPathFiles + 'images/' + imageName,
                    function (fileEntry) {
                        fileEntry.file(
                            function (entry) {
                                return context.dispatch('downloadLabelImage', { uri: label.image, finalPath: context.rootGetters.getPathFiles + 'images/' + imageName, label: label }).then(() => {
                                    return context.dispatch('queueDownloadImages', queue)
                                })
                            },
                            function (e) {
                                return context.dispatch('downloadLabelImage', { uri: label.image, finalPath: context.rootGetters.getPathFiles + 'images/' + imageName, label: label }).then(() => {
                                    return context.dispatch('queueDownloadImages', queue)
                                })
                            }
                        )
                    },
                    function (e) {
                        return context.dispatch('downloadLabelImage', { uri: label.image, finalPath: context.rootGetters.getPathFiles + 'images/' + imageName, label: label }).then(() => {
                            return context.dispatch('queueDownloadImages', queue)
                        })
                    }
                )
            } else {
                context.commit('setLastLabelUpdate', moment().valueOf())
                resolve()
            }
        })
    },

    downloadLabelImage(context, labelImage) {
        return new Promise((resolve, reject) => {
            var fileTransfer = new FileTransfer()
            fileTransfer.download(
                labelImage.uri,
                labelImage.finalPath,
                async function (entry) {
                    var label = Object.assign(labelImage.label)
                    label.local_image = labelImage.finalPath
                    await db_timerproducts.setItem(label.id, label)
                    context.commit('setLabel', label)
                    resolve()
                },
                function (error) {
                    logError('download error source ' + error.source)
                    logError('download error target ' + error.target)
                    logError('upload error code' + error.code)

                    resolve()
                },
                null, // or, pass false
                {}
            )
        })
    },

    async addTimer(context, params) {
        if (fnCheckConnection()) {
            return api.post('timerproduct/timers/add', params).then(async function (response) {
                if (response && response.status) {
                    params.id = response.data.id
                    params.reference_id = params.product
                    params.data = JSON.parse(params.data)
                    params.status = 1
                    context.commit('setTimer', params)
                    context.dispatch('updateCategories')
                    return true
                } else {
                    await queueOfflineTimer(context, params)
                    return false
                }
            })
        } else {
            await queueOfflineTimer(context, params)
        }
    },
    addTimerLocal(context, params) {
        params.id = '0'
        params.isLoading = true
        params.platform = 2
        params.product = params.label.id
        params.quantity = 1
        params.reference_id = params.label.id
        params.same_product_status = 0
        params.state = params.selectedState.id
        params.status = 1
        params.timeToPrintW = null
        params.prep_date = moment().valueOf()
        params.data.date_timestamp = moment().add(5, 'hours').valueOf()

        context.commit('setTimer', params)
        context.dispatch('updateCategories')
    },

    async moveTimerNextState(context, params) {
        let timer = JSON.parse(JSON.stringify(params.timer))

        const previousChannel = timer.data && timer.data.channel ? parseInt(timer.data.channel, 10) : 1
        const { nextState, channel, nextChannelNum } = parseNextStateChannel(params.nextState, timer)
        let lastState = timer.state
        timer.state = nextState

        let nextStateData = context.getters.getProducts(timer.reference_id).states[nextState]
        let currentStateData = context.getters.getProducts(timer.reference_id).states[lastState]

        let data = timer.data

        const lateralChannelMove = isChannelOnlyMove(lastState, nextState, previousChannel, nextChannelNum)
        const preserveTimerDates = params.preserveTimerDates === true || lateralChannelMove
        const originalDateSnapshot = preserveTimerDates && params.timer.data ? captureTimerDateSnapshot(JSON.parse(JSON.stringify(params.timer.data))) : null

        if (!lateralChannelMove) {
            data.moved_from_state = lastState
        }

        if (!preserveTimerDates) {
            populatePrintDateFields(data, context, nextState, channel)
        } else {
            const stateColumn = context.getters.getStates.filter((state) => {
                return state.id == nextState || (channel && state.id == nextState + '_channel' + channel)
            })[0]
            if (stateColumn) {
                data.state = stateColumn.name
            }
        }

        data.note1 = nextStateData.info && nextStateData.info.note1 ? nextStateData.info.note1 : ''
        data.note2 = nextStateData.info && nextStateData.info.note2 ? nextStateData.info.note2 : ''
        data.note3 = nextStateData.info && nextStateData.info.note3 ? nextStateData.info.note3 : ''
        data.is_timer = '●'

        let date = moment(data.date_timestamp * 1000)

        if (!preserveTimerDates) {
            if (parseInt(nextStateData.order) < parseInt(currentStateData.order)) {
                const productRefBackward = context.getters.getProducts(timer.reference_id)
                const timeLimit = parseInt(store.state.login.config.timer_product_time_out !== undefined ? store.state.login.config.timer_product_time_out : 5)
                const now = moment().unix()
                const lastMove = timer.data.last_move_timestamp || 0
                const elapsedMinutes = (now - lastMove) / 60

                const parentId = getBackwardMoveParentId(timer)
                const allTimers = context.state.timers
                const parentExists = parentId && Object.values(allTimers).some((t) => t.id === parentId && t.quantity >= 1)

                if (timeLimit === 0) {
                    return handleBackwardMoveNoTimeLimit(context, timer, lastState, nextStateData, nextState, parentId, parentExists, allTimers, productRefBackward)
                }

                if (elapsedMinutes <= timeLimit) {
                    const withinResult = await handleBackwardMoveWithinLimit(context, timer, lastState, nextStateData, nextState, parentExists, now, data, date)
                    if (withinResult === true) {
                        return true
                    }
                    date = withinResult.date
                } else {
                    return handleBackwardMoveExceeded(context, timer, lastState, nextStateData, nextState)
                }
            } else {
                const productRef = context.getters.getProducts(timer.reference_id)
                date = handleForwardMove(data, nextStateData, currentStateData, productRef, date)
            }
        }

        data.quantity = timer.quantity_move
        data.quantity_avaibles = timer.quantity_move

        if (!preserveTimerDates) {
            populateWeekdayFields(data)
        }

        timer.data = data

        if (preserveTimerDates && originalDateSnapshot) {
            applyTimerDateSnapshot(data, originalDateSnapshot)
            syncTimerDateStringsFromTimestamps(data)
            timer.data = data
        }

        return persistMovedTimer(context, timer, nextState, data, params, { preserveTimerDates, originalDateSnapshot })
    },
    async createNewTimerNotParent(context, { timer, lastState, nextStateData, nextState }) {
        const data = JSON.parse(JSON.stringify(timer.data))
        const serverTimeDifference = context.rootGetters['getServerTimeDifference']
        const clientNow = moment()
        const adjustedNow = moment(clientNow).add(serverTimeDifference, 'minutes')

        const currentTime = adjustedNow.format('YYYY-MM-DD HH:mm:ss')
        const printDate = moment(currentTime, 'YYYY-MM-DD HH:mm:ss')
        const productRef = context.getters.getProducts(timer.reference_id)
        const states = productRef && productRef.states ? productRef.states : {}
        const lastOrderNum = getStateOrder(states, lastState)
        const nextOrderNum = getStateOrder(states, nextState)
        const isBackwardMove = !isNaN(lastOrderNum) && !isNaN(nextOrderNum) && nextOrderNum < lastOrderNum

        let expirationDate = moment(printDate).add(nextStateData.time, 'minutes')
        // can_move_past: misma regla que merge con padre en moveTimerNextState (sin ventana de timeout):
        // si hay snapshot de caducidad inicial, usarlo al crear huérfano aunque el retroceso sea tras time_out.
        if (referenceCanMovePast(productRef) && isBackwardMove && data.caducidad_estado_inicial_timestamp) {
            expirationDate = moment(data.caducidad_estado_inicial_timestamp * 1000)
        }
        const prepDate = moment(printDate).add(nextStateData.preptime, 'minutes')
        const now = moment()

        const formatWeekdays = (date) => {
            let result = {}
            const day = date.isoWeekday()
            for (let i = 1; i <= 7; i++) {
                result[`d${i}`] = i === day ? 'x' : ' '
            }
            return result
        }

        const formatWD = (label, date) => {
            const dayName = i18n.t('calendar.days')[date.day()].toUpperCase()
            return {
                [`${label}_wd`]: dayName,
                [`${label}_wd1`]: dayName[0],
                [`${label}_wd2`]: dayName.slice(0, 2),
                [`${label}_wd3`]: dayName.slice(0, 3)
            }
        }

        const fullData = {
            ...data,
            timer_rel_id: timer.id,
            employee: data.employee,
            employee_name: data.employee_name,
            product: data.product,
            category: data.category,
            account: data.account,
            location: data.location,
            note: '',
            note1: nextStateData.info && nextStateData.info.note1 ? nextStateData.info.note1 : '',
            note2: nextStateData.info && nextStateData.info.note2 ? nextStateData.info.note2 : '',
            note3: nextStateData.info && nextStateData.info.note3 ? nextStateData.info.note3 : '',
            print_date: printDate.format('DD/MM/YYYY HH:mm'),
            print_date_day: printDate.format('DD/MM/YYYY'),
            print_date_time: printDate.format('HH:mm'),
            print_date_timestamp: printDate.unix(),
            currentday: now.format('DD/MM/YYYY 00:00'),
            currentday_day: now.format('DD/MM/YYYY'),
            currentday_time: '00:00',
            currentday_timestamp: moment(now.format('DD/MM/YYYY 00:00'), 'DD/MM/YYYY HH:mm').unix(),
            currenttime: printDate.format('DD/MM/YYYY HH:mm'),
            currenttime_day: printDate.format('DD/MM/YYYY'),
            currenttime_time: printDate.format('HH:mm'),
            currenttime_timestamp: printDate.unix(),
            date: expirationDate.format('DD/MM/YYYY HH:mm'),
            date_day: expirationDate.format('DD/MM/YYYY'),
            date_time: expirationDate.format('HH:mm'),
            date_timestamp: expirationDate.unix(),
            date_raw: expirationDate.format('DD/MM/YYYY HH:mm'),
            date_raw_day: expirationDate.format('DD/MM/YYYY'),
            date_raw_time: expirationDate.format('HH:mm'),
            date_raw_timestamp: expirationDate.unix(),
            prep_date: prepDate.format('DD/MM/YYYY HH:mm'),
            prep_date_day: prepDate.format('DD/MM/YYYY'),
            prep_date_time: prepDate.format('HH:mm'),
            prep_date_timestamp: prepDate.unix(),
            state: nextStateData.name,
            preptime: nextStateData.preptime,
            config: null,
            digitalState: true,
            autoDateCalc: false,
            quantity: timer.quantity_move || 1,
            quantity_avaibles: timer.quantity_move || 1,
            bUpdatedPrintDate: false,
            visible: true,
            package: '',
            caducidad: '',
            caducidad_day: '',
            caducidad_time: '',
            caducidad_timestamp: 48,
            color_relation: 8,
            ...formatWeekdays(printDate),
            ...Object.fromEntries(Object.entries(formatWeekdays(expirationDate)).map(([k, v]) => [`date_${k}`, v])),
            ...Object.fromEntries(Object.entries(formatWeekdays(prepDate)).map(([k, v]) => [`prep_date_${k}`, v])),
            ...formatWD('print_date', printDate),
            ...formatWD('date', expirationDate),
            ...formatWD('prep_date', prepDate),
            ...formatWD('currentday', now),
            ...formatWD('currenttime', printDate)
        }

        const newTimer = {
            id: 'tp_' + moment().valueOf(),
            reference_id: timer.reference_id,
            product: timer.reference_id,
            state: nextState,
            status: TIMERPRODUCT_STATUS.AVAILABLE,
            platform: 2,
            quantity: timer.quantity_move || 1,
            timeToPrintW: nextStateData.time * 60 || 0,
            printed_date: printDate.format('YYYY-MM-DD HH:mm:ss'),
            same_product_status: 2,
            isLoading: false,
            data: JSON.stringify(fullData)
        }

        return context.dispatch('addTimer', newTimer)
    },
    deleteTimer(context, params) {
        if (fnCheckConnection()) {
            return api.get('timerproduct/timers/' + params.id + '/remove').then(function (response) {
                if (response && response.status) {
                    context.commit('deleteTimer', params.id)
                    context.dispatch('updateCategories')
                    return true
                } else {
                    return false
                }
            })
        } else {
            db_timerlogs.removeItem(params.id)
            context.commit('deleteTimer', params)
            return context.dispatch('updateCategories')
        }
    },

    async updateTimer(context, params) {
        // BUILD-6787: Cuando viene `verify_relations`, recalculamos `letter_relation`
        // y lo incluimos en el payload para que el backend quede sincronizado
        // (p. ej. al borrar el último hijo, el padre debe perder la letra en BD).
        const enrichedParams = enrichParamsWithLetterRelation(context.state.timers, params)

        if (fnCheckConnection()) {
            return api.post('timerproduct/timers/' + enrichedParams.id + '/update', enrichedParams.data).then(function (response) {
                if (response && response.status) {
                    context.commit('updateTimer', enrichedParams)
                    context.dispatch('updateCategories')
                    return true
                } else {
                    return false
                }
            })
        } else {
            const existingTimer = context.state.timers[enrichedParams.id]
            const baseData = existingTimer && existingTimer.data ? { ...existingTimer.data } : {}
            const incoming = enrichedParams.data || {}
            const mergedData = { ...baseData }
            Object.keys(incoming).forEach((key) => {
                if (incoming[key] !== undefined) {
                    mergedData[key] = incoming[key]
                }
            })

            let priorPatch = {}
            const prevQueued = await db_timerlogs.getItem(enrichedParams.id)
            if (prevQueued && prevQueued.timerproduct_offline_update === true && prevQueued.data && typeof prevQueued.data === 'object') {
                priorPatch = { ...prevQueued.data }
            }

            const syncPatchData = { ...priorPatch }
            Object.keys(incoming).forEach((key) => {
                if (incoming[key] !== undefined) {
                    syncPatchData[key] = incoming[key]
                }
            })

            await db_timerlogs.setItem(enrichedParams.id, {
                ...enrichedParams,
                data: syncPatchData,
                timerproduct_offline_merged_data: mergedData,
                timerproduct_offline_update: true
            })
            context.commit('updateTimer', { ...enrichedParams, data: mergedData })
            return context.dispatch('updateCategories')
        }
    },
    deleteTimersTemporarily(context, params) {
        if (fnCheckConnection()) {
            const idsTimersDelete = params.timers
            idsTimersDelete.forEach((timer) => {
                context.commit('deleteTimer', timer.id)
                context.dispatch('updateCategories')
            })
            return true
        } else {
            return context.dispatch('updateCategories')
        }
    },
    deleteTimerTemporarily(context, params) {
        if (fnCheckConnection()) {
            context.commit('deleteTimer', params.timerId)
            context.dispatch('updateCategories')

            return true
        } else {
            return context.dispatch('updateCategories')
        }
    },
    async deleteTimers(context, params) {
        if (fnCheckConnection()) {
            const timersDeleted = params.timers

            if (!timersDeleted || !Array.isArray(timersDeleted) || timersDeleted.length === 0) {
                return false
            }

            const timerIds = timersDeleted.map((timer) => getTimerId(timer)).filter((id) => id !== null) // Filtrar IDs inválidos

            // Validar que haya IDs válidos antes de llamar a la API
            if (timerIds.length === 0) {
                return false
            }

            // BUILD-6787: identificar los padres referenciados por los hijos a borrar
            // antes del POST, para luego poder sincronizar su letter_relation en BD.
            const parentIdsToVerify = collectParentIdsFromTimers(timersDeleted)

            const timers = { timers: timerIds }

            return api
                .post('timerproduct/timers/removes', timers)
                .then(async function (response) {
                    if (response && response.status) {
                        // Verificar que el backend devolvió los IDs eliminados
                        const deletedTimerIds = response.deleteTimers || []

                        // Si el backend no devolvió todos los IDs, usar los que enviamos
                        const timersToDelete = deletedTimerIds.length > 0 ? deletedTimerIds : timerIds

                        // Procesar todas las eliminaciones (los commits son síncronos)
                        timersToDelete.forEach((timerId) => {
                            context.commit('updateTimer', { id: timerId, verify_relations: timersDeleted, delete_all: true })
                            context.commit('deleteTimer', timerId)
                            context.commit('purgeDeletedTimer', timerId)
                        })

                        // BUILD-6787: tras eliminar los hijos del store, si algún padre
                        // se quedó sin hijos enlazados, emitir la actualización al backend
                        // para limpiar su `letter_relation` (de lo contrario al refrescar
                        // la app reaparecía la letra antigua).
                        if (params.syncParentLetters !== false) {
                            await syncOrphanedParentsLetterRelation(context, parentIdsToVerify, timersToDelete)
                        }

                        // Actualizar categorías una sola vez después de todas las eliminaciones
                        context.dispatch('updateCategories')
                        return true
                    } else {
                        return false
                    }
                })
                .catch(function (error) {
                    logError('Error en deleteTimers action:', error)
                    return false
                })
        } else {
            return context.dispatch('updateCategories')
        }
    },
    async syncOrphanedParentLettersAfterBulkDelete(context, params) {
        const timers = (params && params.timers) || []
        if (!Array.isArray(timers) || timers.length === 0) {
            return
        }
        const deletedIds = (params && params.deletedIds) || timers.map((timer) => getTimerId(timer)).filter((id) => id !== null)
        const parentIds = (params && params.parentIds) || collectParentIdsFromTimers(timers)
        await syncOrphanedParentsLetterRelation(context, parentIds, deletedIds)
    },
    /**
     * BUILD-7491: elimina todos los expirados de Desechar reutilizando `deleteTimers`.
     * No muta el flujo de borrado existente (undo / selección múltiple).
     * Sincroniza letter_relation de padres de los lotes que sí se borraron,
     * aunque algún lote falle.
     *
     * @returns {Promise<boolean>}
     */
    async deleteExpiredTimers(context) {
        const expiredTimers = collectExpiredTimers(context.state.timers)
        if (expiredTimers.length === 0) {
            return true
        }

        if (!fnCheckConnection()) {
            return false
        }

        const chunks = chunkTimerList(expiredTimers, DELETE_EXPIRED_CHUNK_SIZE)
        const results = await Promise.all(
            chunks.map(async (chunk) => {
                const deleted = await context.dispatch('deleteTimers', { timers: chunk, syncParentLetters: false })
                return { chunk, deleted: deleted === true }
            })
        )

        const succeededTimers = []
        for (let i = 0; i < results.length; i++) {
            if (results[i].deleted) {
                succeededTimers.push(...results[i].chunk)
            }
        }

        if (succeededTimers.length > 0) {
            await context.dispatch('syncOrphanedParentLettersAfterBulkDelete', {
                timers: succeededTimers,
                deletedIds: succeededTimers.map((timer) => getTimerId(timer)).filter((id) => id !== null)
            })
        }

        return succeededTimers.length === expiredTimers.length
    },
    restoreTimers(context, params) {
        if (fnCheckConnection()) {
            const timersToRestore = params.timers
            // Los timers pueden venir como objetos completos o como IDs
            // Filtrar null/undefined antes de procesar para evitar errores
            timersToRestore.forEach((timer) => {
                const timerId = getTimerId(timer)
                // Solo procesar si el timerId es válido (no null)
                if (timerId !== null) {
                    context.commit('restoreTimer', timerId)
                }
            })
            context.dispatch('updateCategories')
        } else {
            return context.dispatch('updateCategories')
        }
    }
}

function format(context, raw) {
    var categories = {
        false: { id: false, name: i18n.t('timersproduct.categories.all'), selected: false, qty: 0, visible: false }
    }
    var products = {}
    var states = []
    var currentStatesSetted = []

    const location_channels = context.rootGetters['getLocationChannels']

    var found = false

    for (var k in raw) {
        var cat = raw[k]
        cat.visible = false
        cat.selected = false
        cat.qty = 0
        if (!found) {
            // cat.selected = true;
            found = true
        }
        categories[cat.id] = cat

        for (var i in cat.references) {
            var reference = cat.references[i]
            reference.category_id = cat.id
            products[reference.id] = reference

            for (var key in reference.states) {
                if (typeof currentStatesSetted[key] === 'undefined') {
                    const state = reference.states[key]
                    if (state.config && state.config.channels && state.config.channels > 1) {
                        let current_channel = 1
                        let channels = location_channels ? (location_channels < state.config.channels ? location_channels : state.config.channels) : state.config.channels
                        while (current_channel <= channels) {
                            states.push({ id: state.id + '_channel' + current_channel, name: state.name + ' ' + current_channel, channel: current_channel, qty: 0, color: 'ok', order: parseInt(state.order) })
                            current_channel++
                        }
                    } else {
                        states.push({ id: reference.states[key].id, name: reference.states[key].name, qty: 0, color: 'ok', order: parseInt(reference.states[key].order) })
                    }
                    currentStatesSetted[key] = true
                }
            }
        }
    }

    states.push({ id: false, name: i18n.t('timersproduct.states.to_delete'), qty: 0, color: 'error', order: 9999 })

    states.sort((a, b) => a.order - b.order)

    return { categories: categories, products: products, states: states }
}

function getCurrentTime(context) {
    let response = ''
    let date = moment()

    const serverTimeDifference = context.rootGetters['getServerTimeDifference']
    const timezone = context.rootGetters['getTimeZoneName']
    const dateLimitation = context.rootGetters['getDateLimitation']

    const options = {
        timeZone: timezone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false
    }

    const copy = moment(date)
    const dateTimeFormat = new Intl.DateTimeFormat([], options).formatToParts()
    const objectDate = {}

    for (const item of dateTimeFormat) {
        objectDate[item.type] = item.value
    }

    const dateString = `${objectDate['day'].padStart(2, '0')}/${objectDate['month'].padStart(2, '0')}/${objectDate['year']} ${objectDate['hour'].padStart(2, '0')}:${objectDate['minute'].padStart(2, '0')}:${objectDate['second'].padStart(2, '0')}`

    date = moment(dateString, 'DD/MM/YYYY HH:mm:ss')

    if (dateLimitation == 0 && date.diff(copy, 'minutes') != serverTimeDifference) {
        const difference = serverTimeDifference - date.diff(copy, 'minutes')
        date = date.add(difference, 'minutes')
    }

    response = date.format('YYYY-MM-DD HH:mm:ss')

    return response
}

function UpdateLetterTimerRelations(timer, timers) {
    const relatedTimerIds = Object.values(timers)
        .filter((t) => {
            if (t.data.timers_relations) {
                const relations = JSON.parse(t.data.timers_relations)
                return relations.some((relation) => relation.id_timer_rel === timer)
            }
            return false
        })
        .map((t) => t.id)

    return relatedTimerIds
}

function parseTimerRelations(data) {
    if (!data || !data.timers_relations) {
        return null
    }
    try {
        const relations = typeof data.timers_relations === 'string' ? JSON.parse(data.timers_relations) : data.timers_relations
        return Array.isArray(relations) ? relations : null
    } catch (e) {
        return null
    }
}

function timerLinksToParent(timer, parentId) {
    const relations = parseTimerRelations(timer && timer.data)
    if (!relations) {
        return false
    }
    return relations.some((rel) => rel && String(rel.id_timer_rel) === String(parentId))
}

function parentHasLinkedChildren(parentId, activeTimers, deletedTimers, excludeChildIds) {
    const excludeSet = new Set((excludeChildIds || []).map(String))
    const pools = [activeTimers, deletedTimers]
    for (const pool of pools) {
        if (!pool) {
            continue
        }
        for (const [id, timer] of Object.entries(pool)) {
            if (excludeSet.has(String(id))) {
                continue
            }
            if (timerLinksToParent(timer, parentId)) {
                return true
            }
        }
    }
    return false
}

function verifyTimerRelations(timer, timers) {
    const relatedTimers = Object.values(timers).filter((t) => timerLinksToParent(t, timer.id))
    if (relatedTimers.length > 0) {
        return timer.data.letter_relation
    }
    return ''
}

/**
 * BUILD-6787: clona `params` añadiendo `letter_relation` recalculado al payload
 * cuando la llamada usa `verify_relations`. Así el backend recibe la letra
 * coherente (vacía si ya no quedan hijos enlazados al padre).
 */
function enrichParamsWithLetterRelation(timers, params) {
    if (!params || !params.verify_relations || params.delete_all) {
        return params
    }
    const targetTimer = timers[params.id]
    if (!targetTimer) {
        return params
    }
    const data = { ...(params.data || {}) }
    const computedLetter = verifyTimerRelations(targetTimer, timers)
    if (data.letter_relation === undefined) {
        data.letter_relation = computedLetter
    }
    return { ...params, data }
}

/**
 * BUILD-6787: extrae los ids de los padres referenciados en `timers_relations`
 * de cada timer hijo. Acepta tanto la cadena JSON como el array ya parseado.
 */
function collectParentIdsFromTimers(timers) {
    const parentIds = new Set()
    if (!Array.isArray(timers)) {
        return parentIds
    }
    timers.forEach((timer) => {
        const data = timer && timer.data
        if (!data || !data.timers_relations) {
            return
        }
        let relations
        try {
            relations = typeof data.timers_relations === 'string' ? JSON.parse(data.timers_relations) : data.timers_relations
        } catch (e) {
            return
        }
        if (!Array.isArray(relations)) {
            return
        }
        relations.forEach((rel) => {
            if (rel && rel.id_timer_rel) {
                parentIds.add(rel.id_timer_rel)
            }
        })
    })
    return parentIds
}

/**
 * BUILD-6787: tras una eliminación bulk, sincroniza con el backend la letra
 * de los padres que se quedaron sin hijos enlazados. Reusa la acción
 * `updateTimer` con `verify_relations: true`, que ya inyecta el
 * `letter_relation` recalculado en el payload del POST.
 */
async function syncOrphanedParentsLetterRelation(context, parentIds, deletedIds) {
    if (!parentIds || parentIds.size === 0) {
        return
    }
    const deletedSet = new Set((deletedIds || []).map(String))
    const promises = []
    parentIds.forEach((parentId) => {
        // El padre también puede haber sido eliminado en el mismo batch
        if (deletedSet.has(String(parentId))) {
            return
        }
        const parentTimer = context.state.timers[parentId]
        if (!parentTimer) {
            return
        }
        // Solo sincronizar cuando no quedan hijos enlazados (activos o pendientes
        // de borrar en otros chunks). No usar letter_relation vacía del store como
        // señal de huérfano: verifyTimerRelations la devuelve también si aún hay hijos.
        const hasLinkedChildren = parentHasLinkedChildren(parentId, context.state.timers, context.state.deletedTimers, deletedIds)
        if (!hasLinkedChildren) {
            promises.push(
                context.dispatch('updateTimer', {
                    id: parentId,
                    verify_relations: true,
                    data: {}
                })
            )
        }
    })
    if (promises.length === 0) {
        return
    }
    try {
        await Promise.all(promises)
    } catch (e) {
        logError('Error sincronizando letter_relation de padres tras eliminación bulk:', e)
    }
}

const mutations = {
    setStates(state, data) {
        state.states = data
    },
    setCategories(state, data) {
        state.categories = data
    },
    setTimer(state, data) {
        // Nueva referencia: Vue 2 no notifica al añadir una clave y reasignar el mismo objeto.
        Vue.set(state, 'timers', { ...state.timers, [data.id]: data })
    },
    setTimers(state, data) {
        state.timers = data
    },
    deleteTimer(state, data) {
        let timers = state.timers
        const deletedTimer = timers[data]
        if (deletedTimer) {
            Vue.set(state.deletedTimers, data, deletedTimer)
        }
        delete timers[data]
        // Usar Vue.set para asegurar reactividad correcta
        Vue.set(state, 'timers', { ...timers })
    },
    purgeDeletedTimer(state, timerId) {
        if (state.deletedTimers[timerId]) {
            Vue.delete(state.deletedTimers, timerId)
        }
    },
    updateTimer(state, data) {
        let timers = state.timers
        let timersDeleted = state.deletedTimers
        if (data) {
            if (data.data && timers[data.id]) {
                if (data.data.quantity_avaibles !== undefined && data.data.quantity_avaibles !== null && data.data.quantity_avaibles !== '') {
                    Vue.set(timers[data.id].data, 'quantity_avaibles', data.data.quantity_avaibles)
                }

                if (data.data.quantity !== undefined && data.data.quantity !== null && data.data.quantity !== '') {
                    Vue.set(timers[data.id].data, 'quantity', data.data.quantity)
                }

                if (data.data.timers_relations) {
                    timers[data.id].data.timers_relations = data.data.timers_relations
                }
                if (data.data.color_relation) {
                    timers[data.id].data.color_relation = data.data.color_relation
                }
                // BUILD-6787: aceptamos también cadena vacía (limpieza explícita
                // de la letra, p. ej. cuando ya no quedan hijos enlazados).
                if (data.data.letter_relation !== undefined && data.data.letter_relation !== null) {
                    timers[data.id].data.letter_relation = data.data.letter_relation
                }
                if (data.data.caducidad_ultimo_estado_timestamp != null) {
                    timers[data.id].data.caducidad_ultimo_estado_timestamp = data.data.caducidad_ultimo_estado_timestamp
                }
                if (data.data.caducidad_estado_inicial_timestamp != null) {
                    timers[data.id].data.caducidad_estado_inicial_timestamp = data.data.caducidad_estado_inicial_timestamp
                }
                applyTimerDateFieldsFromPatch(timers[data.id].data, data.data)
            }

            if (data.verify_relations) {
                if (data.delete_all) {
                    const timersDeleted = data.verify_relations
                    let relatedTimerId
                    Object.keys(timersDeleted).forEach((key) => {
                        const timer = timersDeleted[key]
                        if (timer.data && timer.data.timers_relations) {
                            const timerRelations = JSON.parse(timer.data.timers_relations)
                            if (timerRelations.length > 0) {
                                relatedTimerId = timerRelations[0].id_timer_rel
                            }
                            let hasRelatedTimer = false
                            Object.keys(timers).forEach((key) => {
                                const timer = timers[key]
                                if (timer.data && timer.data.timers_relations) {
                                    const timerRelations = JSON.parse(timer.data.timers_relations)
                                    if (timerRelations.length > 0 && timerRelations[0].id_timer_rel === relatedTimerId) {
                                        hasRelatedTimer = true
                                    }
                                }
                            })
                            if (!hasRelatedTimer && timers[relatedTimerId]) {
                                timers[relatedTimerId].data.letter_relation = ''
                            }
                        } else {
                            const relatedTimerIds = UpdateLetterTimerRelations(data.id, timers)
                            relatedTimerIds.forEach((id) => {
                                if (timers[id]) {
                                    timers[id].data.letter_relation = ''
                                }
                            })
                        }
                    })
                } else {
                    if (data.data.quantity_avaibles === 0) {
                        const relatedTimerIds = UpdateLetterTimerRelations(data.id, timers)
                        relatedTimerIds.forEach((id) => {
                            if (timers[id]) {
                                timers[id].data.letter_relation = ''
                            }
                        })
                    } else {
                        timers[data.id].data.letter_relation = verifyTimerRelations(timers[data.id], timers)
                    }
                }
            }
        }
        Vue.set(state, 'timers', { ...timers })
    },
    restoreTimer(state, timerId) {
        const restoredTimer = state.deletedTimers[timerId]
        // Verificar que el timer existe antes de acceder a sus propiedades
        if (restoredTimer) {
            restoredTimer.isSelected = false
            state.timers[timerId] = restoredTimer
            Vue.delete(state.deletedTimers, timerId)
        }
    },
    setInterval(state, data) {
        state.interval = data
    },
    setProducts(state, data) {
        state.products = data

        var ordered = {}
        var aux = Object.values(data)
        aux.sort((a, b) => a.name.localeCompare(b.name, 'es', { numeric: true }))
        for (var i in aux) {
            var lab = aux[i]
            ordered[lab.id] = lab
        }
        state.productsOrdered = ordered
    },

    selectCategory(state, category) {
        if (category == false || category == 'false') {
            for (var k in state.categories) {
                var cat = state.categories[k]
                cat.selected = false
            }
        } else {
            for (var k in state.categories) {
                var cat = state.categories[k]
                if (cat.id == category.id) {
                    cat.selected = true
                } else {
                    cat.selected = false
                }
            }
        }
        Vue.set(state, 'categories', { ...state.categories })
    },
    setActiveKpiFilter(state, filter) {
        state.activeKpiFilter = filter
    },
    setRecommendationDismissed(state, dismissed) {
        state.recommendationDismissed = Boolean(dismissed)
    },
    toggleTimerSelected(state, payload) {
        const { timerId, forceTrue, forcefalse } = payload
        if (state.timers[timerId]) {
            Vue.set(state.timers[timerId], 'isSelected', forceTrue ? true : forcefalse ? false : !state.timers[timerId].isSelected)
        }
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
